import { useEffect, useState } from "react";

// Resolves a library (precompiled) progression's display name from its id.
// The ExerciseProgression source only stores `precompiledProgressionId`, so the
// programmazione form/detail need this to surface the progression name. Results
// are cached module-side so multiple exercises referencing the same library
// progression don't each fire a request.
//
// The API client is imported dynamically inside the effect (not statically) so
// consumers of this hook don't drag the axios-based client into their module
// graph — keeping pure-function unit tests of those components transform-free.
const cache = new Map<string, string>();

export function usePrecompiledProgressionName(
  id: string | null | undefined,
): string | null {
  const [name, setName] = useState<string | null>(
    id ? cache.get(id) ?? null : null,
  );

  useEffect(() => {
    if (!id) {
      setName(null);
      return;
    }
    const cached = cache.get(id);
    if (cached) {
      setName(cached);
      return;
    }
    let active = true;
    import("../api/Entities")
      .then(({ Entities }) => {
        if (typeof Entities?.getPrecompiledProgression !== "function") {
          if (active) setName(null);
          return;
        }
        return Entities.getPrecompiledProgression(id).then(({ data }) => {
          const label = data.progression?.label ?? null;
          if (label) cache.set(id, label);
          if (active) setName(label);
        });
      })
      .catch(() => {
        if (active) setName(null);
      });
    return () => {
      active = false;
    };
  }, [id]);

  return name;
}
